import React, { useState, memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import { MenuVerticalIcon } from '~/assets/icons';
import styles from './PriceItemList.module.scss';
import classNames from 'classnames/bind';
import { BaseTable } from '~/components/common';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { HeadCelType, SortDirection } from '~/utils/types/common';
import MenuOption from '~/components/price-items/menu-option/MenuOption';
import { useTranslation } from 'react-i18next';
import usePermission from '~/hooks/usePermission';
import { priceItemActions } from '~/thunks/price-item/priceItemsSlice';
import { IPriceItem, IPriceItemList } from '~/utils/interface/priceItem';
import { formatCurrency } from '~/utils/helper';
import { PERMISSIONS } from '~/utils/constants/permission';

const cx = classNames.bind(styles);

type PriceItemListProps = {
  data?: IPriceItem;
  onOpenEdit: (id: string) => void;
  onOpenView: (id: string) => void;
};

function PriceItemList(props: PriceItemListProps) {
  const permissions = usePermission();
  const { t } = useTranslation(['admin/price-item']);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const headCells: Array<HeadCelType> = useMemo(
    () => [
      {
        id: 'code',
        label: t('properties.code'),
        sortable: true,
        searchable: true,
      },
      {
        id: 'title',
        label: t('properties.title'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'price',
        label: t('properties.priceList'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'priceComponent',
        label: t('properties.component'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'unit',
        label: t('properties.unit'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'unitPrice',
        label: t('properties.unitPrice'),
        sortable: true,
        searchable: false,
      },
      {
        id: 'action',
        label: t('properties.action'),
        sortable: false,
        searchable: false,
      },
    ],
    [t]
  );

  const [menuSelectedId, setMenuSelectedId] = useState<string | undefined>(undefined);
  const { priceItems, filter, currentPage, totalPages } = useAppSelector((state) => state.priceItem);
  const dispatch = useAppDispatch();

  const openOptionMenu = (event: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
    dispatch(priceItemActions.changePriceItemId(itemId));
    setAnchorEl(event.currentTarget);
    setMenuSelectedId(itemId);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setMenuSelectedId(undefined);
  };

  const setSelectedIds = useCallback(
    (selected: Array<string>) => {
      dispatch(priceItemActions.changeSelectedIds(selected));
    },
    [dispatch]
  );

  const handleEditItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenEdit(menuSelectedId);
    }
  };

  const handleViewItem = async () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      props.onOpenView(menuSelectedId);
    }
  };

  const handleDeleteItem = () => {
    handleClose();
    if (menuSelectedId !== undefined) {
      dispatch(priceItemActions.changeMenuSelectId(menuSelectedId));
      dispatch(priceItemActions.changeOpenAlertDelete(true));
    }
  };

  const handleChangePage = (newPage: number) => {
    dispatch(
      priceItemActions.setFilter({
        page: newPage,
      })
    );
  };

  const handleRequestSort = (sortBy: Array<{ orderBy: string; order: SortDirection }>) => {
    dispatch(
      priceItemActions.setFilter({
        sort: sortBy.map((item) => ({
          field: item.orderBy,
          type: item.order,
        })),
      })
    );
  };
  return (
    <Box className={cx('price-component-list-container')}>
      <BaseTable<IPriceItemList>
        data={priceItems}
        setSelectedIds={setSelectedIds}
        headCells={headCells}
        onChangePage={handleChangePage}
        onRequestSort={handleRequestSort}
        page={currentPage}
        totalPages={totalPages}
        sort={filter.sort}
        renderCell={(priceItem: IPriceItemList) => {
          return (
            <>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{priceItem.code}</Box>
                </Box>
              </TableCell>

              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{priceItem.title}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{priceItem.price?.name}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{priceItem.priceComponent?.name}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{priceItem.unitLabel}</Box>
                </Box>
              </TableCell>
              <TableCell component='th' scope='row' padding='none'>
                <Box className={cx('price-component-container')}>
                  <Box className={cx('priceComponentName')}>{formatCurrency(priceItem.unitPrice)}</Box>
                </Box>
              </TableCell>
              {(permissions[PERMISSIONS.priceItem_edit] || permissions[PERMISSIONS.priceItem_delete]) && (
                <TableCell>
                  <Box className={cx('deadline-container')}>
                    <IconButton
                      onClick={(event) => {
                        openOptionMenu(event, priceItem.id);
                      }}
                    >
                      <MenuVerticalIcon className='w-[19px] h-[19px]' />
                    </IconButton>
                  </Box>
                </TableCell>
              )}
            </>
          );
        }}
      />
      <MenuOption
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        handleEditItem={handleEditItem}
        handleDeleteItem={handleDeleteItem}
        handleViewItem={handleViewItem}
      />
    </Box>
  );
}

export default memo(PriceItemList);
